import type {
  ListPaymentsQuery,
  PaymentResponse,
} from '@monorepo/shared/schemas/payments'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  AlertCircle,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  ExternalLink,
  Eye,
  MoreHorizontal,
  Receipt,
  XCircle,
  Zap,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import {
  AddPaymentDialog,
  PaymentDetailsDialog,
  PaymentFilters,
  type PaymentFiltersType,
  paymentsService,
} from '@/features/payments'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/payments')({
  component: PaymentsPage,
})

function PaymentsPage() {
  const navigate = useNavigate()
  const { currentEnvironment } = useEnvironment()
  const [payments, setPayments] = useState<PaymentResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<PaymentFiltersType>({})
  const [selectedPayment, setSelectedPayment] =
    useState<PaymentResponse | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  const loadPayments = useCallback(
    async (activeFilters?: PaymentFiltersType) => {
      setLoading(true)
      try {
        const params: Partial<ListPaymentsQuery> = {
          expand: 'customer',
        }

        if (
          activeFilters?.filter_field &&
          activeFilters?.filter_operator &&
          activeFilters?.filter_value
        ) {
          params.filter_field = activeFilters.filter_field
          params.filter_operator = activeFilters.filter_operator
          params.filter_value = activeFilters.filter_value
        }

        if (activeFilters?.payment_status?.length) {
          params.status = activeFilters
            .payment_status[0] as ListPaymentsQuery['status']
        }

        if (activeFilters?.payment_method?.length) {
          params.payment_method = activeFilters
            .payment_method[0] as ListPaymentsQuery['payment_method']
        }

        const result = await paymentsService.listPayments(params)
        setPayments(result.data)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      } finally {
        setLoading(false)
      }
    },
    [currentEnvironment?.id]
  )

  useEffect(() => {
    loadPayments(filters)
  }, [filters, loadPayments])

  const getStatusVariant = (status: PaymentResponse['status']) => {
    switch (status) {
      case 'succeeded':
        return 'default'
      case 'pending':
        return 'secondary'
      case 'failed':
        return 'destructive'
      case 'refunded':
      case 'canceled':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: PaymentResponse['status']) => {
    switch (status) {
      case 'succeeded':
        return <CheckCircle className='w-4 h-4' />
      case 'pending':
        return <Clock className='w-4 h-4' />
      case 'failed':
        return <XCircle className='w-4 h-4' />
      case 'refunded':
      case 'canceled':
        return <AlertCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const getStatusLabel = (status: PaymentResponse['status']) => {
    if (status === 'succeeded') return 'Completed'
    if (status === 'canceled') return 'Canceled'
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  const getPaymentTypeIcon = (type: string) => {
    switch (type) {
      case 'credit_card':
        return <CreditCard className='w-4 h-4' />
      case 'bank_transfer':
        return <Zap className='w-4 h-4' />
      case 'check':
      case 'cash':
      case 'other':
        return <Building className='w-4 h-4' />
      default:
        return <CreditCard className='w-4 h-4' />
    }
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleViewPaymentDetails = (payment: PaymentResponse) => {
    setSelectedPayment(payment)
  }

  const handleViewInvoice = (payment: PaymentResponse) => {
    if (payment.invoice_id) {
      navigate({
        to: '/invoice-detail/$invoiceId',
        params: { invoiceId: payment.invoice_id },
      } as never)
    }
  }

  const handleFiltersChange = (newFilters: PaymentFiltersType) => {
    setFilters(newFilters)
  }

  const handleReset = () => {
    setFilters({})
  }

  const handlePaymentCreated = () => {
    loadPayments(filters)
  }

  // Define table columns
  const paymentColumns: ColumnDef<PaymentResponse>[] = [
    {
      accessorKey: 'payment_id',
      header: 'Payment Number',
      size: 180,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>{row.original.payment_id}</div>
          <div className='text-sm text-muted-foreground'>
            #{row.original.payment_id}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      size: 250,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {row.original.customer?.name ?? '—'}
          </div>
          <div className='text-sm text-muted-foreground'>
            {row.original.customer?.email ?? 'No email'}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'organization_id',
      header: 'Organization',
      size: 250,
      cell: ({ row }) => (
        <div className='text-sm'>{row.original.organization_id ?? '—'}</div>
      ),
    },
    {
      accessorKey: 'processed_at',
      header: 'Payment Date',
      size: 180,
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Calendar className='w-4 h-4 text-muted-foreground' />
          <span className='text-sm'>
            {formatDate(row.original.processed_at || row.original.created_at)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'payment_method',
      header: 'Payment Method',
      size: 180,
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          {getPaymentTypeIcon(row.original.payment_method ?? '')}
          <span className='text-sm capitalize'>
            {(row.original.payment_method ?? '—').replace('_', ' ')}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 120,
      cell: ({ row }) => (
        <div>
          <div className='font-medium'>
            {formatCurrency(row.original.amount, row.original.currency)}
          </div>
          {row.original.refunded_amount > 0 && (
            <div className='text-sm text-red-600'>
              -{formatCurrency(row.original.refunded_amount)} refunded
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'invoice_id',
      header: 'Invoice ID',
      size: 180,
      cell: ({ row }) => (
        <div>
          {row.original.invoice_id ? (
            <div className='flex items-center gap-1'>
              <Receipt className='w-4 h-4 text-muted-foreground' />
              <span className='text-sm font-medium'>
                {row.original.invoice_id}
              </span>
            </div>
          ) : (
            <span className='text-sm text-muted-foreground'>-</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => (
        <Badge
          variant={getStatusVariant(row.original.status)}
          className='gap-1'
        >
          {getStatusIcon(row.original.status)}
          {getStatusLabel(row.original.status)}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
              <MoreHorizontal className='w-4 h-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-48'>
            <DropdownMenuItem
              onClick={() => handleViewPaymentDetails(row.original)}
            >
              <Eye className='w-4 h-4 mr-2' />
              View Details
            </DropdownMenuItem>
            {row.original.invoice_id && (
              <DropdownMenuItem onClick={() => handleViewInvoice(row.original)}>
                <ExternalLink className='w-4 h-4 mr-2' />
                View Invoice
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  if (loading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='space-y-6'>
          {/* Header - Static, no skeleton */}
          <div className='flex justify-between items-center'>
            <div>
              <h1 className='text-3xl font-bold'>Payments</h1>
              <p className='text-muted-foreground'>
                Track payments and billing history across all customers
              </p>
            </div>
            <AddPaymentDialog onPaymentCreated={handlePaymentCreated} />
          </div>

          {/* Filters - Static, no skeleton */}
          <PaymentFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSearch={() => {}}
            onReset={handleReset}
          />

          {/* Table Skeleton - Only for dynamic data */}
          <div className='bg-transparent border rounded-lg'>
            <div className='animate-pulse space-y-4 p-6'>
              <div className='grid grid-cols-8 gap-4 pb-4 border-b'>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={i}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                <div key={i} className='grid grid-cols-8 gap-4 py-3'>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={j}
                      className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex justify-between items-center'>
          <div>
            <h1 className='text-3xl font-bold'>Payments</h1>
            <p className='text-muted-foreground'>
              Track payments and billing history across all customers
            </p>
          </div>
          <AddPaymentDialog onPaymentCreated={handlePaymentCreated} />
        </div>

        {/* Filters */}
        <PaymentFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={() => {}}
          onReset={handleReset}
        />

        {/* Payments Table */}
        <div className='bg-transparent border rounded-lg'>
          {payments.length > 0 ? (
            <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
              <VirtualizedTable
                data={payments}
                columns={paymentColumns.map(col => ({
                  ...col,
                  enableSorting: false,
                }))}
                containerHeight='calc(100vh - 380px)'
                className='bg-transparent'
                emptyMessage='No payments found. Try adjusting your filters.'
                loadingMessage='Loading more payments...'
                enableSorting={true}
                enableColumnResizing={true}
                enableColumnReordering={true}
                enableColumnPinning={false}
              />
            </div>
          ) : (
            <div className='text-center py-12 text-muted-foreground'>
              <DollarSign className='w-12 h-12 mx-auto mb-4 opacity-50' />
              <h3 className='text-lg font-medium mb-2'>No payments found</h3>
              <p className='text-sm max-w-sm mx-auto mb-4'>
                No payments have been processed yet. Payment history will appear
                here once transactions are completed.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        payment={selectedPayment}
        onClose={() => setSelectedPayment(null)}
      />
    </div>
  )
}
